import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import '../src/app.css'; // or a separate CSS file
import logo from './assets/logo3.png';

function CoitCards() {
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  
  // Data from pipeline
  const [folderData, setFolderData] = useState(null);
  const [mergedData, setMergedData] = useState(null);
  const [finalMerge, setFinalMerge] = useState(null);

  // For random checks
  const [randomSample, setRandomSample] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [sampleIndex, setSampleIndex] = useState(0);

  // A ref to the log container for auto-scrolling
  const logContainerRef = useRef(null);
  const cleanCell = s => s.trim().replace(/^"+|"+$/g, '');

  // Whenever logs change, scroll the log container to the bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Helper logger
  const addLog = (message) => {
    setLogs((prev) => [...prev, message]);
    console.log(message);
  };

  // File selection
  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    addLog(`${e.target.files.length} file(s) selected.`);
  };

  // Clear log
  const clearLogs = () => {
    setLogs([]);
    setRandomSample([]);
    setShowModal(false);
    addLog('Logs cleared.');
  };

  // Start Process: (1) Convert -> (2) Merge -> (3) Combine
  const startProcess = async () => {
    if (files.length === 0) {
      addLog('No .txt files selected.');
      return;
    }
    // 1) Convert
    addLog('--- START: Converting files ---');
    const newFolderData = {}; 
    for (const file of files) {
      try {
        const text = await file.text();
        const baseName = file.name.replace(/\.[^.]+$/, '');
        const parts = baseName.split('_');
        let folderName = 'Converted';
        if (parts.length >= 4) {
          const category = parts[0];         
          const ref = parts[parts.length - 2] + '_' + parts[parts.length - 1]; 
          folderName = `${category}_${ref}`;
        }
        const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
        if (lines.length === 0) {
          addLog(`File ${file.name} is empty or invalid.`);
          continue;
        }

        const header = lines[0].split('|').map(h => h.trim());
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split('|');
const rowObj = {};
header.forEach((colName, idx) => {
  const raw = cols[idx] || '';
  rowObj[colName] = cleanCell(raw);
});
          rows.push(rowObj);
        }

        if (!newFolderData[folderName]) {
          newFolderData[folderName] = [];
        }
        newFolderData[folderName].push({
          filename: baseName + '.csv',
          csvRows: rows,
          header
        });
        addLog(`Converted "${file.name}" -> folder "${folderName}" in memory.`);
      } catch (err) {
        addLog(`Error converting file "${file.name}": ${err}`);
      }
    }
    setFolderData(newFolderData);
    addLog('--- FINISH: Conversion ---');

    // 2) Auto-merge
    addLog('--- START: Auto-Merge ---');
    const mergedResults = {}; // { folderName -> array of { filename, csvContent } }

    Object.entries(newFolderData).forEach(([folderName, filesInFolder]) => {
      let letterFile = null;
      let couponFile = null;

      filesInFolder.forEach((f) => {
        if (/letter/i.test(f.filename)) {
          letterFile = f;
        } else if (/coupon/i.test(f.filename)) {
          couponFile = f;
        }
      });

      if (!letterFile || !couponFile) {
        addLog(`Folder "${folderName}": missing letter or coupon file. Skipping merge.`);
        return;
      }

      addLog(`Merging in "${folderName}" -> Letter="${letterFile.filename}", Coupon="${couponFile.filename}"`);

      // Build coupon dictionary
      const couponDict = {};
      couponFile.csvRows.forEach((row) => {
        const frn = cleanCell(row['FileRecordNumber'] || '');
        if (!frn) return;
        const couponCode  = (row['CouponCode']  || '').trim();
        const couponValue = (row['CouponValue'] || '').trim();
        if (!couponDict[frn]) couponDict[frn] = [];
        couponDict[frn].push([couponCode, couponValue]);
      });

      // Merge letter
      const outputRows = [];
      let processedCount = 0;
      let recordsWithCoupon = 0;
      let recordsWithoutCoupon = 0;

      letterFile.csvRows.forEach((row) => {
        processedCount++;
        const frn = cleanCell(row['FileRecordNumber'] || '');
        if (couponDict[frn]) {
          recordsWithCoupon++;
          const couponEntries = couponDict[frn];
          const couponCodes = couponEntries.map((e) => e[0]).filter(Boolean).join(';');
          const firstCouponValue = couponEntries[0][1] || '0';

          // Remove BarCodes
          delete row['BarCode1'];
          delete row['BarCode2'];
          delete row['BarCode3'];
          delete row['BarCode4'];
          delete row['BarCode5'];

          // totalValue
          let totalValue = '';
          try {
            const tb = parseFloat(row['TotalBarCodes'] || '0');
            const cv = parseFloat(firstCouponValue || '0');
            totalValue = (tb * cv).toString();
          } catch {
            totalValue = '';
          }

          row['CouponCode']  = couponCodes;
          row['CouponValue'] = firstCouponValue;
          row['TotalValue']  = totalValue;
          outputRows.push(row);
        } else {
          recordsWithoutCoupon++;
        }
      });

      addLog(`Folder "${folderName}": processed=${processedCount}, matched=${recordsWithCoupon}, noCoupons=${recordsWithoutCoupon}`);

      // Reorder columns
      const orderedFields = [
        "FileRecordNumber","CustomerNumber","LetterType","FirstName","LastName",
        "Address1","Address2","City","State","Zip","ExpirationDate","BusinessUnit",
        "GM","GMTitle","TotalBarCodes","ReminderFlag","ReminderTotal","VisitTotal",
        "CouponValue","TotalValue","WONumber","PhoneNumber","CustPhoneNumber","CouponCode"
      ];

      // Name
      let outName = letterFile.filename;
      const parts = outName.split('_');
      if (parts.length >= 4 && parts[1].toUpperCase() === "LETTER") {
        parts.splice(1,1);
        outName = "MERGE_" + parts.join('_');
      } else {
        outName = "MERGE_" + outName;
      }

      // Build CSV
      let csvText = orderedFields.join(',') + '\n';
      outputRows.forEach((r) => {
        const line = orderedFields.map((fld) => {
          let val = (r[fld] ?? '').toString();
          if (/["|,]/.test(val)) {
            val = `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(',');
        csvText += line + '\n';
      });

      if (!mergedResults[folderName]) {
        mergedResults[folderName] = [];
      }
      mergedResults[folderName].push({
        filename: outName,
        csvContent: csvText
      });
    });

    setMergedData(mergedResults);
    addLog('--- FINISH: Auto-Merge ---');

    // 3) Combine merges
    addLog('--- START: Combine All Merges ---');
    const combined = combineAllMerges(mergedResults);
    setFinalMerge(combined);
    addLog('--- FINISH: Combine All Merges ---');

    addLog('Process complete. You may check random rows or download all.');
  };

  // Combine merges into "Final Merge.csv"
  const combineAllMerges = (mergedRes) => {
    const combinedRows = [];
    const orderedFields = [
      "FileRecordNumber","CustomerNumber","LetterType","FirstName","LastName",
      "Address1","Address2","City","State","Zip","ExpirationDate","BusinessUnit",
      "GM","GMTitle","TotalBarCodes","ReminderFlag","ReminderTotal","VisitTotal",
      "CouponValue","TotalValue","WONumber","PhoneNumber","CustPhoneNumber","CouponCode"
    ];
    const finalHeader = ["Ref #", ...orderedFields];
    let combinedCsv = finalHeader.join(',') + '\n';

    Object.entries(mergedRes).forEach(([folderName, merges]) => {
      merges.forEach((m) => {
        if (!m.filename.toLowerCase().startsWith('merge_')) return;
        if (!m.filename.toLowerCase().endsWith('.csv')) return;

        // parse CSV to get rows
        const lines = m.csvContent.split('\n').filter(x => x.trim() !== '');
        if (lines.length <= 1) return; // only header or empty
        const header = parseCsvLine(lines[0]);
        const dataLines = lines.slice(1);
        
        // derive refName from filename => remove "MERGE_" prefix, remove ".csv"
        let refName = m.filename.replace(/^MERGE_/i, '').replace(/\.csv$/i, '');

        dataLines.forEach((line) => {
          const cols = parseCsvLine(line);
          const rowDict = {};
          header.forEach((h, idx) => {
            rowDict[h] = cols[idx];
          });
          const frn = rowDict['FileRecordNumber'] || '';
          const refVal = `${refName} - ${frn}`;
          // build final row
          let newRow = { 'Ref #': refVal };
          orderedFields.forEach((f) => {
            newRow[f] = rowDict[f] ?? '';
          });
          combinedRows.push(newRow);
        });
      });
    });

    combinedRows.forEach((row) => {
      const line = finalHeader.map((fld) => {
        let val = (row[fld] ?? '').toString();
        if (/["|,]/.test(val)) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(',');
      combinedCsv += line + '\n';
    });

    addLog(`Combined All Merges: ${combinedRows.length} rows total.`);
    return combinedCsv;
  };

  // Parse a single CSV line, handling quotes
  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i+1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += c;
      }
    }
    result.push(current);
    return result;
  };

  // Pick up to 10 random rows from "Final Merge" and open modal
  const checkRandomRows = () => {
    if (!finalMerge) {
      addLog('No Final Merge available. Run "Start Process" first.');
      return;
    }
    const lines = finalMerge.split('\n').filter(l => l.trim() !== '');
    if (lines.length <= 1) {
      addLog('Final Merge has no data.');
      return;
    }
    const header = parseCsvLine(lines[0]); 
    const dataLines = lines.slice(1);

    const sampleSize = Math.min(10, dataLines.length); 
    const chosenIndices = new Set();
    while (chosenIndices.size < sampleSize) {
      const randIndex = Math.floor(Math.random() * dataLines.length);
      chosenIndices.add(randIndex);
    }

    const sample = [];
    chosenIndices.forEach((idx) => {
      const rowLine = dataLines[idx];
      const cols = parseCsvLine(rowLine);
      const rowObj = {};
      header.forEach((h, i) => {
        rowObj[h] = cols[i];
      });
      sample.push(rowObj);
    });
    setRandomSample(sample);
    setShowModal(true);
    setSampleIndex(0);
    addLog(`Showing a random sample of ${sampleSize} row(s) from Final Merge for manual checking.`);
  };

  // "Close" the popup
  const closeModal = () => {
    setShowModal(false);
  };

  // "Next" record in the popup
  const goNext = () => {
    if (sampleIndex < randomSample.length - 1) {
      setSampleIndex(sampleIndex + 1);
    }
  };

  // "Previous" record in the popup
  const goPrev = () => {
    if (sampleIndex > 0) {
      setSampleIndex(sampleIndex - 1);
    }
  };

  // Download everything as ZIP
  const downloadAll = async () => {
    if (!folderData) {
      addLog('No folder data found. Please run "Start Process" first.');
      return;
    }
    addLog('Packaging all data into a single ZIP...');

    const zip = new JSZip();

    // A) Original converted files
    Object.entries(folderData).forEach(([folderName, items]) => {
      const sub = zip.folder(folderName);
      items.forEach((item) => {
        let text = item.header.join(',') + '\n';
        item.csvRows.forEach((row) => {
          const line = item.header.map((h) => {
            let v = (row[h] ?? '').toString();
            if (/["|,]/.test(v)) {
              v = `"${v.replace(/"/g, '""')}"`;
            }
            return v;
          }).join(',');
          text += line + '\n';
        });
        sub.file(item.filename, text);
      });
    });

    // B) Merged
    if (mergedData) {
      Object.entries(mergedData).forEach(([folderName, merges]) => {
        const sub = zip.folder(folderName);
        merges.forEach((m) => {
          sub.file(m.filename, m.csvContent);
        });
      });
    }

    // C) Final Merge
    if (finalMerge) {
      zip.file('Final Merge.csv', finalMerge);
    }

    try {
      const blob = await zip.generateAsync({ type: 'blob' });
      const fileName = `COITCards_${new Date().toISOString().slice(0,10).replace(/-/g, '')}.zip`;
      saveAs(blob, fileName);
      addLog(`Downloaded all data as ${fileName}`);
    } catch (err) {
      addLog(`Error generating ZIP: ${err}`);
    }
  };

  // If we're showing the modal, figure out the current row object
  const currentSample = randomSample[sampleIndex] || null;

  return (
    <div className="container">
      {/* "Back to Home" in the top-left corner */}
      <div style={{ position: 'absolute', top: '20px', left: '20px' }}>
       
        
      </div>
      <img src={logo} alt="Logo" className="page-logo" />

      <h1 className="app-title">COIT Postcard Merge Tool</h1>

      <div className="file-input-section">
        <label htmlFor="coitFileInput" className="file-label">
          Select .txt Files:
        </label>
        <input
          id="coitFileInput"
          className="file-input"
          type="file"
          multiple
          accept=".txt"
          onChange={handleFileChange}
        />
      </div>

      <div className="buttons-container">
        <button className="action-button" onClick={startProcess}>
          Start Process
        </button>
        <button className="action-button" onClick={checkRandomRows}>
          Check
        </button>
        <button className="action-button" onClick={downloadAll}>
          Download All
        </button>
        <button className="action-button" onClick={clearLogs}>
          Clear Log
        </button>
      </div>

      {/* The modal overlay, shown if showModal = true */}
      {showModal && currentSample && (
        <div className="modal-overlay">
          <div className="modal-content">
            {/* "X" close button in top-right corner */}
            <button className="modal-close-btn" onClick={closeModal}>
              &times;
            </button>
            
            <h2 className="modal-title">
              Random Record {sampleIndex + 1} of {randomSample.length}
            </h2>

            <div className="modal-body">
              {/* Show each key-value in multiple lines */}
              {Object.entries(currentSample).map(([key, val]) => (
                <p key={key} className="modal-row">
                  <strong>{key}:</strong> {val}
                </p>
              ))}
            </div>

            <div className="modal-footer">
              <button
                onClick={goPrev}
                disabled={sampleIndex <= 0}
                className="modal-nav-btn"
              >
                Prev
              </button>
              <button
                onClick={goNext}
                disabled={sampleIndex >= randomSample.length - 1}
                className="modal-nav-btn"
              >
                Next
              </button>
              <button onClick={closeModal} className="modal-nav-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="log-container" style={{ marginTop: '20px' }}>
        <h2 className="log-title">Log</h2>
        {/* Attach ref for auto-scrolling */}
        <div className="log-messages" ref={logContainerRef}>
          {logs.map((log, i) => (
            <div key={i} className="log-entry">
              {log}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CoitCards;
